import React from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider,
  CircularProgress, Chip, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon            from '@mui/icons-material/Close';
import PersonAddIcon        from '@mui/icons-material/PersonAdd';
import SwapHorizIcon        from '@mui/icons-material/SwapHoriz';
import PersonRemoveIcon     from '@mui/icons-material/PersonRemove';
import AssignmentIndIcon    from '@mui/icons-material/AssignmentInd';

const satoshi = { fontFamily: '"Satoshi", sans-serif' };

const ACTION_META = {
  assigned:   { label: 'Assigned',    color: '#10B981', bgLight: '#D1FAE5', bgDark: 'rgba(16,185,129,0.15)',  Icon: PersonAddIcon     },
  reassigned: { label: 'Reassigned',  color: '#F59E0B', bgLight: '#FEF3C7', bgDark: 'rgba(245,158,11,0.15)', Icon: SwapHorizIcon      },
  unassigned: { label: 'Unassigned',  color: '#EF4444', bgLight: '#FEE2E2', bgDark: 'rgba(239,68,68,0.15)',  Icon: PersonRemoveIcon   },
  claimed:    { label: 'Claimed',     color: '#6366F1', bgLight: '#EEF2FF', bgDark: 'rgba(99,102,241,0.15)', Icon: AssignmentIndIcon  },
  unclaimed:  { label: 'Unclaimed',   color: '#EC4899', bgLight: '#FCE7F3', bgDark: 'rgba(236,72,153,0.15)', Icon: PersonRemoveIcon   },
};

function HistoryEntry({ entry, isDark, textPrimary, textSecondary, border }) {
  const meta = ACTION_META[entry.action] || ACTION_META.assigned;
  const { Icon } = meta;
  const bg   = isDark ? meta.bgDark : meta.bgLight;

  const when = entry.changedAt
    ? new Date(entry.changedAt).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '—';

  const resolveUser = (u) => {
    if (!u) return null;
    if (typeof u === 'string') return u;
    if (u.firstName) return `${u.firstName} ${u.lastName}`;
    return u.email || String(u);
  };

  const toName   = resolveUser(entry.toRadiologist)   || entry.toRadiologistName;
  const fromName = resolveUser(entry.fromRadiologist)  || entry.fromRadiologistName;
  const byName   = resolveUser(entry.changedBy)        || entry.changedByName;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1.75 }}>
      {/* Timeline dot */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%', bgcolor: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: meta.color, flexShrink: 0
        }}>
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, width: '2px', bgcolor: isDark ? 'rgba(255,255,255,0.07)' : '#E2E8F0', mt: 0.5 }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              height: 20, fontSize: '0.68rem', fontFamily: '"Satoshi",sans-serif', fontWeight: 700,
              bgcolor: bg, color: meta.color, border: 'none'
            }}
          />
          <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.72rem' }}>
            {when}
          </Typography>
        </Box>

        {toName && (
          <Typography sx={{ ...satoshi, color: textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
            → {toName}
          </Typography>
        )}
        {fromName && (
          <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.78rem', mt: 0.25 }}>
            from {fromName}
          </Typography>
        )}
        {byName && (
          <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.73rem', mt: 0.25 }}>
            by {byName}
          </Typography>
        )}
        {entry.reason && (
          <Box sx={{
            mt: 0.75, p: '6px 10px', borderRadius: '8px',
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC',
            border: `1px solid ${border}`
          }}>
            <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.75rem', fontStyle: 'italic' }}>
              "{entry.reason}"
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * AssignmentHistoryDrawer
 *
 * Props:
 *   open       – boolean
 *   onClose    – () => void
 *   history    – array of assignment history entries
 *   current    – { radiologistId, radiologistName, assignedAt | claimedAt } | null
 *   title      – string (e.g. patient name or accession)
 *   loading    – boolean
 */
export default function AssignmentHistoryDrawer({
  open,
  onClose,
  history = [],
  current = null,
  title = 'Assignment History',
  loading = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const bg            = isDark ? '#1A1A1A' : '#FFFFFF';
  const border        = isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';
  const textPrimary   = isDark ? '#F1F5F9' : '#0F172A';
  const textSecondary = isDark ? '#CBD5E1' : '#64748B';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 420 },
          bgcolor: bg,
          borderLeft: `1px solid ${border}`,
          boxShadow: isDark ? '-8px 0 32px rgba(0,0,0,0.6)' : '-8px 0 32px rgba(0,0,0,0.08)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        borderBottom: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Box>
          <Typography sx={{ ...satoshi, fontWeight: 700, color: textPrimary, fontSize: '1rem' }}>
            Assignment History
          </Typography>
          {title && (
            <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.78rem', mt: 0.25 }}>
              {title}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small" sx={{ color: textSecondary }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Current assignee banner */}
      {current?.radiologistName && (
        <Box sx={{
          mx: 2.5, mt: 2, p: 1.5, borderRadius: '10px',
          bgcolor: isDark ? 'rgba(13,148,136,0.12)' : '#F0FDFA',
          border: `1px solid ${isDark ? 'rgba(13,148,136,0.3)' : '#99F6E4'}`
        }}>
          <Typography variant="caption" sx={{ ...satoshi, color: isDark ? '#2DD4BF' : '#0F766E', fontWeight: 700, fontSize: '0.68rem' }}>
            CURRENTLY ASSIGNED
          </Typography>
          <Typography sx={{ ...satoshi, color: textPrimary, fontWeight: 700, fontSize: '0.9rem', mt: 0.25 }}>
            {current.radiologistName}
          </Typography>
          {(current.assignedAt || current.claimedAt) && (
            <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.72rem', mt: 0.25 }}>
              since {new Date(current.assignedAt || current.claimedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
            </Typography>
          )}
        </Box>
      )}

      {/* Timeline */}
      <Box sx={{ px: 2.5, pt: 2, pb: 4, overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={28} sx={{ color: '#0D9488' }} />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 6 }}>
            <AssignmentIndIcon sx={{ fontSize: 40, color: textSecondary, opacity: 0.4, mb: 1 }} />
            <Typography sx={{ ...satoshi, color: textSecondary, fontSize: '0.875rem' }}>
              No assignment history yet
            </Typography>
          </Box>
        ) : (
          history.map((entry, i) => (
            <HistoryEntry
              key={i}
              entry={entry}
              isDark={isDark}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              border={border}
            />
          ))
        )}
      </Box>
    </Drawer>
  );
}
